import { useQuery } from '@tanstack/react-query';

import {
    GetDisplayPreferencesResponse,
    UserDisplayPreferences,
    UserDisplayPreferencesExtended,
} from '@/api/userAccount/types';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { useGlobalStore } from '@/store';

const DEFAULT_DISPLAY_PREFERENCES = {
    distance: 'm',
    measuringDistance: 'km',
    speed: 'm/s',
    temperature: 'C',
    timeZone: 'local',
    pressure: 'bar',
} as const;

const addDerivedUnitPreferences = (displayPreferences: UserDisplayPreferences): UserDisplayPreferencesExtended => ({
    // assume some unit preferences based on other unit preferences (but allow overriding if set explicitly)
    precipitationRate: displayPreferences.distance === 'm' ? 'mm/h' : 'in/h',
    directionalWaveEnergy: displayPreferences.distance === 'm' ? 'cm^2/(Hz*Deg)' : 'in^2/(Hz*Deg)',
    airPressure: displayPreferences.pressure === 'bar' ? 'mbar' : 'hPa',
    currentSpeed: displayPreferences.speed === 'knots' ? 'knots' : 'cm/s',
    density: displayPreferences.distance === 'm' ? 'kg/m^3' : 'lb/cuft',
    length: displayPreferences.distance,
    angle: 'deg',
    ...displayPreferences,
});

export const DEFAULT_UNITS = addDerivedUnitPreferences({ ...DEFAULT_DISPLAY_PREFERENCES, csvFormat: '2025-02' });

const publicDisplayPreferences = {
    isPending: false,
    isError: false,
    isIdle: false,
    isSuccess: true,
    data: DEFAULT_UNITS,
} as const;

export const getDisplayPreferences = async (options?: RequestOptions): Promise<UserDisplayPreferencesExtended> => {
    const response = await axios.get<GetDisplayPreferencesResponse, GetDisplayPreferencesResponse>(
        `/profile/get-display-preferences`,
        options
    );
    return addDerivedUnitPreferences({ ...DEFAULT_DISPLAY_PREFERENCES, ...response.data });
};

type UseDisplayPreferencesOptions = {
    config?: QueryConfig<typeof getDisplayPreferences>;
};

export const useDisplayPreferences = ({
    config: { enabled = true, ...config } = {},
}: UseDisplayPreferencesOptions = {}) => {
    // if we're not logged in, the query won't cache the error,
    // so just return a default to avoid retrying any time we need
    // units anywhere in the public view
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);

    const userDisplayPreferences = useQuery<UserDisplayPreferencesExtended>({
        ...config,
        enabled: enabled && isLoggedIn,
        queryKey: ['display-preferences'],
        queryFn: ({ signal }) => getDisplayPreferences({ signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    if (isLoggedIn) {
        return userDisplayPreferences;
    }

    return publicDisplayPreferences;
};

export const usePreferredTimeZone = () => {
    return useDisplayPreferences().data?.timeZone ?? DEFAULT_DISPLAY_PREFERENCES.timeZone;
};
